// src/pages/estoque/HistoricoPedidosPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useIsMobile } from "../../hooks/useIsMobile";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { formatDataHora } from "../../utils/dateFormat";
import FiltroBar from "../../components/FiltroBar/FiltroBar";

function HistoricoPedidosPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const isMobile = useIsMobile();

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [pedidos, setPedidos] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState("pendentes");

  // filtros adicionais
  const [campoFiltro, setCampoFiltro] = useState("requisicaoNumero");
  const [textoFiltro, setTextoFiltro] = useState("");

  const [expandedId, setExpandedId] = useState(null);

  const [modalReceber, setModalReceber] = useState(null);
  const [qtdReceberAgora, setQtdReceberAgora] = useState("");
  const [valorRecebimento, setValorRecebimento] = useState("");
  const [salvandoRecebimento, setSalvandoRecebimento] = useState(false);

  const [modalEditar, setModalEditar] = useState(null);
  const [formEditar, setFormEditar] = useState({
    modelo: "",
    quantidade: "",
    valorUnitario: "",
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const [deletando, setDeletando] = useState(null);

  async function carregarPedidos() {
    if (!usuarioLogado?.grupoId) return;

    setCarregando(true);
    setErro("");
    try {
      // ⚠️ IMPORTANTE (compatibilidade com dados antigos):
      // Algumas bases antigas podem ter pedidos sem empresaId (ou com empresa gravada por nome).
      // Para não "sumir" o histórico após mudanças, buscamos por grupoId e filtramos por empresa no client.
      // (Mantém escopo por grupo e evita perder registros legados.)
      const ref = collection(db, "pedidosMateriais");
      const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
      const snap = await getDocs(q);
      let lista = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Se NÃO estiver em modo global, filtramos localmente por empresa.
      // Aceita:
      // - empresaId igual ao contexto
      // - registros legados sem empresaId, mas com empresaNome igual ao contexto
      if (!modoGlobal && empresaAtual?.id) {
        const norm = (s) =>
          String(s || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim();
        const alvoNome = norm(empresaAtual?.nome);
        lista = lista.filter((p) => {
          if (p.empresaId) return p.empresaId === empresaAtual.id;
          if (p.empresaNome) return norm(p.empresaNome) === alvoNome;
          // Se não tem empresaId nem empresaNome, consideramos legado e mostramos (evita "sumir" tudo)
          return true;
        });
      }

      setPedidos(lista);
    } catch (e) {
      console.error(e);
      setErro("Erro ao carregar pedidos de material.");
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarPedidos();
  }, [usuarioLogado, modoGlobal, empresaAtual]);

  const pedidosFiltrados = useMemo(() => {
    let base = [...pedidos];
    // Pendentes incluem pedidos parcialmente recebidos
    if (filtroStatus === "pendentes")
      base = base.filter((p) => p.statusPedido === "Pendente" || p.statusPedido === "Parcial");
    if (filtroStatus === "recebidos") base = base.filter((p) => p.statusPedido === "Recebido");

    const txt = String(textoFiltro || "").trim().toLowerCase();
    if (txt) {
      base = base.filter((p) => {
        let alvo = "";
        if (campoFiltro === "requisicaoNumero") alvo = p.requisicaoNumero || "";
        else if (campoFiltro === "modelo") alvo = p.modelo || "";
        else if (campoFiltro === "osCodigo") alvo = p.osCodigo || "";
        else if (campoFiltro === "maquina") alvo = p.maquinaNome || "";
        else if (campoFiltro === "empresa") alvo = p.empresaNome || "";
        else alvo = "";

        return String(alvo).toLowerCase().includes(txt);
      });
    }
    base.sort((a, b) => {
      const ta = a.dataPedido?.toMillis ? a.dataPedido.toMillis() : 0;
      const tb = b.dataPedido?.toMillis ? b.dataPedido.toMillis() : 0;
      return tb - ta;
    });
    return base;
  }, [pedidos, filtroStatus, textoFiltro, campoFiltro]);

  function handleRowClick(pedidoId) {
    setExpandedId((prev) => (prev === pedidoId ? null : pedidoId));
  }

  function handleAbrirReceber(pedido) {
    setModalReceber(pedido);
    const total = Number(pedido.quantidade) || 0;
    const recebida = Number(pedido.quantidadeRecebida) || 0;
    const pendente = Math.max(total - recebida, 0);
    setQtdReceberAgora(pendente > 0 ? String(pendente) : "");
    setValorRecebimento(pedido.valorUnitario != null ? String(pedido.valorUnitario) : "");
  }

  function handleFecharReceber() {
    setModalReceber(null);
    setQtdReceberAgora("");
    setValorRecebimento("");
  }

  async function handleConfirmarRecebimento() {
    if (!modalReceber || !usuarioLogado?.grupoId) return;

    const qtdNum = parseFloat(String(qtdReceberAgora || "").replace(",", ".")) || 0;
    const valorInformado = String(valorRecebimento || "").trim();
    const valorNum = valorInformado ? parseFloat(valorInformado.replace(",", ".")) || 0 : null;

    if (qtdNum <= 0) {
      setErro("Informe a quantidade recebida agora.");
      return;
    }

    setSalvandoRecebimento(true);
    setErro("");
    setSucesso("");

    try {
      const pedido = modalReceber;

      await runTransaction(db, async (transaction) => {
        const pedidoRef = doc(db, "pedidosMateriais", pedido.id);
        const pedidoSnap = await transaction.get(pedidoRef);
        if (!pedidoSnap.exists()) {
          throw new Error("Pedido não encontrado.");
        }
        const dadosPedido = pedidoSnap.data();
        if (dadosPedido.statusPedido === "Recebido") {
          return;
        }

        const totalPedido = Number(dadosPedido.quantidade) || 0;
        const recebidaAtual = Number(dadosPedido.quantidadeRecebida) || 0;
        const pendenteAtual = Math.max(totalPedido - recebidaAtual, 0);
        const receberAgora = Math.max(Math.min(qtdNum, pendenteAtual), 0);
        if (receberAgora <= 0) return;

        const valorUnitarioFinal = valorNum != null ? valorNum : (dadosPedido.valorUnitario != null ? Number(dadosPedido.valorUnitario) : 0);
        const novoRecebido = recebidaAtual + receberAgora;
        const terminou = novoRecebido >= totalPedido && totalPedido > 0;
        const novoStatus = terminou ? "Recebido" : "Parcial";
        const valorTotal = valorUnitarioFinal * totalPedido;

        const updatePedido = {
          statusPedido: novoStatus,
          quantidadeRecebida: novoRecebido,
          valorTotal,
          dataUltimoRecebimento: serverTimestamp(),
          // Só marca dataRecebimento quando concluir 100%
          dataRecebimento: terminou ? serverTimestamp() : null,
        };

        // Só sobrescreve valorUnitario se usuário informar (ou se estiver vazio no pedido)
        if (valorNum != null || dadosPedido.valorUnitario == null) {
          updatePedido.valorUnitario = valorUnitarioFinal;
        }

        transaction.update(pedidoRef, updatePedido);

        if (dadosPedido.controleEstoque) {
          const filtros = [
            where("grupoId", "==", dadosPedido.grupoId),
            where("modelo", "==", dadosPedido.modelo),
          ];
          if (dadosPedido.empresaId) {
            filtros.push(where("empresaId", "==", dadosPedido.empresaId));
          }

          const refEstoque = collection(db, "estoqueInterno");
          const qEstoque = query(refEstoque, ...filtros);
          const snapEstoque = await getDocs(qEstoque);

          if (snapEstoque.empty) {
            const novoRef = doc(refEstoque);
            transaction.set(novoRef, {
              grupoId: dadosPedido.grupoId,
              grupoNome: dadosPedido.grupoNome || null,
              empresaId: dadosPedido.empresaId || null,
              empresaNome: dadosPedido.empresaNome || null,
              modelo: dadosPedido.modelo,
              controleEstoque: true,
              quantidadeAtual: receberAgora,
              valorMedio: valorUnitarioFinal || null,
              atualizadoEm: serverTimestamp(),
            });
          } else {
            const docEstoque = snapEstoque.docs[0];
            const dadosEstoque = docEstoque.data();
            const novaQuantidade =
              (dadosEstoque.quantidadeAtual || 0) + receberAgora;
            transaction.update(docEstoque.ref, {
              quantidadeAtual: novaQuantidade,
              atualizadoEm: serverTimestamp(),
            });
          }
        }
      });

      if (pedido.osId) {
        const refPedidosOs = collection(db, "pedidosMateriais");
        const qPedidosOs = query(
          refPedidosOs,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("osId", "==", pedido.osId)
        );
        const snapPedidosOs = await getDocs(qPedidosOs);

        const todosRecebidos = snapPedidosOs.docs.every((d) => {
          const dados = d.data() || {};
          return dados.statusPedido === "Recebido";
        });

        if (todosRecebidos) {
          const osRef = doc(db, "ordensServico", pedido.osId);
          const osSnap = await getDoc(osRef);
          if (osSnap.exists()) {
            const osDados = osSnap.data() || {};
            const statusAtual = osDados.statusId || osDados.status || "";
            const statusNomeAtual = osDados.statusNome || "";
            const statusAtualNorm = String(statusAtual || "").toLowerCase().trim();
            const statusNomeNorm = String(statusNomeAtual || "")
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .trim();

            const estaAguardandoMaterial =
              statusAtualNorm === "fab_aguard_mat" ||
              statusAtualNorm === "fab_aguard_material" ||
              statusNomeNorm === "aguardando material";

            // Regra: só volta para "Aberta" automaticamente quando TODAS as peças da OS chegarem
            // e a OS estiver em "Aguardando Material" (não sobrescreve outros status)
            if (estaAguardandoMaterial) {
              await updateDoc(osRef, {
                statusId: "fab_aberta",
                statusNome: "Aberta",
                statusCor: "#2563eb",
                atualizadoEm: serverTimestamp(),
              });
            }
          }
        }
      }

      setSucesso("Recebimento confirmado com sucesso.");
      handleFecharReceber();
      await carregarPedidos();
    } catch (e) {
      console.error(e);
      setErro("Erro ao confirmar recebimento. Tente novamente.");
    } finally {
      setSalvandoRecebimento(false);
    }
  }

  function handleAbrirEditar(pedido) {
    setModalEditar(pedido);
    setFormEditar({
      modelo: pedido.modelo || "",
      quantidade: pedido.quantidade != null ? String(pedido.quantidade) : "",
      valorUnitario: pedido.valorUnitario != null ? String(pedido.valorUnitario) : "",
    });
  }

  function handleFecharEditar() {
    setModalEditar(null);
    setFormEditar({ modelo: "", quantidade: "", valorUnitario: "" });
  }

  async function handleSalvarEdicao() {
    if (!modalEditar) return;

    const modelo = formEditar.modelo.trim();
    const quantidade = parseFloat(formEditar.quantidade.replace(",", ".")) || 0;
    const valorUnitario = parseFloat(formEditar.valorUnitario.replace(",", ".")) || 0;

    if (!modelo) {
      setErro("Informe o modelo/descrição do material.");
      return;
    }

    setSalvandoEdicao(true);
    setErro("");

    try {
      const ref = doc(db, "pedidosMateriais", modalEditar.id);

      // Mantém consistência caso o usuário edite a quantidade abaixo do que já foi recebido
      const recebidaAtual = Number(modalEditar.quantidadeRecebida) || 0;
      const novaRecebida = Math.min(recebidaAtual, quantidade);
      const novoStatus =
        quantidade > 0
          ? novaRecebida >= quantidade
            ? "Recebido"
            : novaRecebida > 0
              ? "Parcial"
              : "Pendente"
          : "Pendente";

      await updateDoc(ref, {
        modelo,
        quantidade,
        quantidadeRecebida: novaRecebida,
        valorUnitario,
        valorTotal: quantidade * valorUnitario,
        statusPedido: novoStatus,
        dataRecebimento:
          novoStatus === "Recebido"
            ? (modalEditar.dataRecebimento || serverTimestamp())
            : null,
        atualizadoEm: serverTimestamp(),
      });

      setSucesso("Pedido atualizado com sucesso.");
      handleFecharEditar();
      await carregarPedidos();
    } catch (e) {
      console.error(e);
      setErro("Erro ao atualizar pedido. Tente novamente.");
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function handleDeletar(pedido) {
    const confirmar = window.confirm(
      `Deseja excluir o pedido "${pedido.modelo || pedido.id}"?`
    );
    if (!confirmar) return;

    setDeletando(pedido.id);
    setErro("");

    try {
      const ref = doc(db, "pedidosMateriais", pedido.id);
      await deleteDoc(ref);

      setSucesso("Pedido excluído com sucesso.");
      await carregarPedidos();
    } catch (e) {
      console.error(e);
      setErro("Erro ao excluir pedido.");
    } finally {
      setDeletando(null);
    }
  }

  const tituloPagina = "Histórico de Pedidos";

  const conteudoTabela = (
    <div className="pc-table-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: 8,
        }}
      >
        <div>
          <label className="pc-label" style={{ marginRight: 8 }}>
            Mostrar:
          </label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="pc-input"
            style={{ maxWidth: 200 }}
          >
            <option value="pendentes">Somente pendentes</option>
            <option value="recebidos">Somente recebidos</option>
            <option value="todos">Todos (histórico)</option>
          </select>
        </div>
      </div>

      <FiltroBar
        campos={[
          { value: "requisicaoNumero", label: "Requisição" },
          { value: "modelo", label: "Modelo" },
          { value: "osCodigo", label: "OS" },
          { value: "maquina", label: "Máquina" },
          { value: "empresa", label: "Empresa" },
        ]}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        placeholder="Filtrar histórico de pedidos..."
        mostrarAdicionar={false}
      />

      <table className="pc-table">
        <thead>
          <tr>
            <th>Data pedido</th>
            <th>Req.</th>
            <th>Modelo / Descrição</th>
            <th>Qtd</th>
            <th>Valor unit.</th>
            <th>Valor total</th>
            <th>OS vinculada</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {pedidosFiltrados.length === 0 && (
            <tr>
              <td colSpan={9} style={{ textAlign: "center", padding: 16 }}>
                Nenhum pedido localizado para o filtro selecionado.
              </td>
            </tr>
          )}

          {pedidosFiltrados.map((p) => {
            const isExpanded = expandedId === p.id;
            const isPendente = p.statusPedido === "Pendente" || p.statusPedido === "Parcial";
            const recebida = Number(p.quantidadeRecebida) || 0;
            const total = Number(p.quantidade) || 0;

            return (
              <React.Fragment key={p.id}>
                <tr
                  onClick={() => handleRowClick(p.id)}
                  style={{ cursor: "pointer" }}
                  className={isExpanded ? "row-expanded" : ""}
                >
                  <td>{p.dataPedido ? formatDataHora(p.dataPedido) : "-"}</td>
                  <td>{p.requisicaoNumero || "-"}</td>
                  <td>{p.modelo || "-"}</td>
                  <td>
                    {p.statusPedido === "Parcial" ? `${recebida} / ${total}` : (p.quantidade ?? "-")}
                  </td>
                  <td>
                    {p.valorUnitario != null
                      ? `R$ ${Number(p.valorUnitario).toFixed(2)}`
                      : "-"}
                  </td>
                  <td>
                    {p.valorTotal != null
                      ? `R$ ${Number(p.valorTotal).toFixed(2)}`
                      : "-"}
                  </td>
                  <td>{p.osCodigo || "-"}</td>
                  <td>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: "0.75em",
                        fontWeight: 600,
                        background: isPendente
                          ? "rgba(234, 179, 8, 0.15)"
                          : "rgba(34, 197, 94, 0.15)",
                        color: isPendente ? "#eab308" : "#22c55e",
                      }}
                    >
                      {p.statusPedido || "-"}
                    </span>
                  </td>
                  <td>
                    <div
                      style={{ display: "flex", gap: 4 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isPendente && (
                        <button
                          className="pc-btn-icon"
                          title="Receber"
                          onClick={() => handleAbrirReceber(p)}
                          style={{ padding: 6, color: "#22c55e" }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                      )}
                      <button
                        className="pc-btn-icon"
                        title="Editar"
                        onClick={() => handleAbrirEditar(p)}
                        style={{ padding: 6 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="pc-btn-icon"
                        title="Deletar"
                        onClick={() => handleDeletar(p)}
                        disabled={deletando === p.id}
                        style={{
                          padding: 6,
                          color: deletando === p.id ? "#94a3b8" : "#ef4444",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="row-details-expanded">
                    <td colSpan={9} style={{ padding: 0 }}>
                      <div
                        style={{
                          background: "rgba(15, 23, 42, 0.4)",
                          borderTop: "1px solid rgba(148, 163, 184, 0.1)",
                          borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                          padding: "16px 20px",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: 16,
                          }}
                        >
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              REQUISIÇÃO
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.requisicaoNumero || "-"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              EMPRESA
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.empresaNome || "-"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              SETOR
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.setorNome || "-"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              MÁQUINA
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.maquinaNome || "-"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              CONTROLE ESTOQUE
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.controleEstoque ? "Sim" : "Não"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                              ORIGEM
                            </div>
                            <div style={{ fontSize: "0.9em" }}>
                              {p.origem || "-"}
                            </div>
                          </div>
                          {p.dataRecebimento && (
                            <div>
                              <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                DATA RECEBIMENTO
                              </div>
                              <div style={{ fontSize: "0.9em" }}>
                                {formatDataHora(p.dataRecebimento)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const conteudo = (
    <div className="pc-page">
      <header className="pc-page-header">
        <div>
          <h1>{tituloPagina}</h1>
          <p className="pc-page-subtitle">
            Acompanhe os pedidos de material. Clique na linha para ver mais detalhes.
          </p>
        </div>
      </header>

      {erro && (
        <div className="pc-alert pc-alert-erro" style={{ marginBottom: 12 }}>
          {erro}
        </div>
      )}
      {sucesso && (
        <div className="pc-alert pc-alert-sucesso" style={{ marginBottom: 12 }}>
          {sucesso}
        </div>
      )}

      {carregando && <p>Carregando pedidos...</p>}
      {!carregando && conteudoTabela}

      {modalReceber && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleFecharReceber}
        >
          <div
            className="pc-card"
            style={{ minWidth: 340, maxWidth: 400, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 16 }}>Confirmar Recebimento</h3>

            <div
              style={{
                background: "rgba(30, 41, 59, 0.5)",
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "grid", gap: 8 }}>
                <div>
                  <span style={{ fontSize: "0.75em", opacity: 0.6 }}>Material:</span>
                  <div style={{ fontWeight: 500 }}>{modalReceber.modelo || "-"}</div>
                </div>
                <div style={{ display: "flex", gap: 16 }}>
                  <div>
                    <span style={{ fontSize: "0.75em", opacity: 0.6 }}>Quantidade:</span>
                    <div>
                      {(Number(modalReceber.quantidadeRecebida) || 0)} / {(Number(modalReceber.quantidade) || 0)}
                      <span style={{ marginLeft: 8, fontSize: "0.85em", opacity: 0.65 }}>
                        (pendente: {Math.max((Number(modalReceber.quantidade) || 0) - (Number(modalReceber.quantidadeRecebida) || 0), 0)})
                      </span>
                    </div>
                  </div>
                  {modalReceber.osCodigo && (
                    <div>
                      <span style={{ fontSize: "0.75em", opacity: 0.6 }}>OS:</span>
                      <div>{modalReceber.osCodigo}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="pc-label">Quantidade recebida agora</label>
              <input
                type="number"
                min="0"
                step="1"
                className="pc-input"
                value={qtdReceberAgora}
                onChange={(e) => setQtdReceberAgora(e.target.value)}
                placeholder="0"
                autoFocus
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="pc-label">Valor Unitário (R$)</label>
              <input
                type="text"
                className="pc-input"
                value={valorRecebimento}
                onChange={(e) => setValorRecebimento(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleFecharReceber}
                disabled={salvandoRecebimento}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pc-btn-primario"
                onClick={handleConfirmarRecebimento}
                disabled={salvandoRecebimento}
              >
                {salvandoRecebimento ? "Salvando..." : "Confirmar Recebimento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalEditar && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleFecharEditar}
        >
          <div
            className="pc-card"
            style={{ minWidth: 340, maxWidth: 400, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 16 }}>Editar Pedido</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label className="pc-label">Modelo / Descrição</label>
                <input
                  type="text"
                  className="pc-input"
                  value={formEditar.modelo}
                  onChange={(e) =>
                    setFormEditar((prev) => ({ ...prev, modelo: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="pc-label">Quantidade</label>
                <input
                  type="text"
                  className="pc-input"
                  value={formEditar.quantidade}
                  onChange={(e) =>
                    setFormEditar((prev) => ({ ...prev, quantidade: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="pc-label">Valor Unitário (R$)</label>
                <input
                  type="text"
                  className="pc-input"
                  value={formEditar.valorUnitario}
                  onChange={(e) =>
                    setFormEditar((prev) => ({ ...prev, valorUnitario: e.target.value }))
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleFecharEditar}
                disabled={salvandoEdicao}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pc-btn-primario"
                onClick={handleSalvarEdicao}
                disabled={salvandoEdicao}
              >
                {salvandoEdicao ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo={tituloPagina}
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div className="m-form-container">
          {erro && <div className="m-alert m-alert-erro">{erro}</div>}
          {sucesso && <div className="m-alert m-alert-sucesso">{sucesso}</div>}

          {/* Filtro */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <div className="m-form-group" style={{ marginBottom: 0 }}>
              <label className="m-form-label">Mostrar:</label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="m-form-input"
              >
                <option value="pendentes">Somente pendentes</option>
                <option value="recebidos">Somente recebidos</option>
                <option value="todos">Todos (histórico)</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={campoFiltro}
                onChange={(e) => setCampoFiltro(e.target.value)}
                className="m-form-input"
                style={{ maxWidth: 160 }}
              >
                <option value="requisicaoNumero">Requisição</option>
                <option value="modelo">Modelo</option>
                <option value="osCodigo">OS</option>
                <option value="maquina">Máquina</option>
                <option value="empresa">Empresa</option>
              </select>
              <input
                className="m-form-input"
                placeholder="Filtrar..."
                value={textoFiltro}
                onChange={(e) => setTextoFiltro(e.target.value)}
              />
            </div>
          </div>

          {carregando ? (
            <p style={{ padding: 16, textAlign: "center" }}>Carregando...</p>
          ) : pedidosFiltrados.length === 0 ? (
            <p style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>
              Nenhum pedido encontrado.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {pedidosFiltrados.map((p) => {
                const isPendente = p.statusPedido === "Pendente" || p.statusPedido === "Parcial";
                const recebida = Number(p.quantidadeRecebida) || 0;
                const total = Number(p.quantidade) || 0;
                return (
                  <div key={p.id} className="m-form-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: "1rem", flex: 1 }}>
                        {p.modelo || "Sem descrição"}
                      </div>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          background: isPendente ? "rgba(234, 179, 8, 0.15)" : "rgba(34, 197, 94, 0.15)",
                          color: isPendente ? "#eab308" : "#22c55e",
                        }}
                      >
                        {p.statusPedido || "-"}
                      </span>
                    </div>

                    <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginBottom: 8 }}>
                      {p.dataPedido ? formatDataHora(p.dataPedido) : "-"}
                      {p.requisicaoNumero && ` | Req: ${p.requisicaoNumero}`}
                      {p.osCodigo && ` | OS: ${p.osCodigo}`}
                    </div>

                    <div className="m-form-row" style={{ marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Qtd</div>
                        <div style={{ fontWeight: 600 }}>
                          {p.statusPedido === "Parcial" ? `${recebida} / ${total}` : (p.quantidade ?? "-")}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Valor Unit.</div>
                        <div style={{ fontWeight: 600 }}>
                          {p.valorUnitario != null ? `R$ ${Number(p.valorUnitario).toFixed(2)}` : "-"}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Total</div>
                        <div style={{ fontWeight: 600 }}>
                          {p.valorTotal != null ? `R$ ${Number(p.valorTotal).toFixed(2)}` : "-"}
                        </div>
                      </div>
                    </div>

                    {/* Ações */}
                    <div style={{ display: "flex", gap: 8, borderTop: "1px solid var(--cor-borda)", paddingTop: 10 }}>
                      {isPendente && (
                        <button
                          className="m-btn-secondary-sm"
                          style={{ flex: 1, color: "#22c55e", borderColor: "rgba(34, 197, 94, 0.3)" }}
                          onClick={() => handleAbrirReceber(p)}
                        >
                          Receber
                        </button>
                      )}
                      <button
                        className="m-btn-secondary-sm"
                        style={{ flex: 1 }}
                        onClick={() => handleAbrirEditar(p)}
                      >
                        Editar
                      </button>
                      <button
                        className="m-btn-secondary-sm"
                        style={{ flex: 1, color: "#ef4444", borderColor: "rgba(239, 68, 68, 0.3)" }}
                        onClick={() => handleDeletar(p)}
                        disabled={deletando === p.id}
                      >
                        {deletando === p.id ? "..." : "Excluir"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Receber - Mobile */}
        {modalReceber && (
          <div className="m-modal-overlay" onClick={handleFecharReceber}>
            <div className="m-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: 12, fontSize: "1.1rem" }}>Confirmar Recebimento</h3>
              <p style={{ fontSize: "0.9rem", color: "#94a3b8", marginBottom: 16 }}>
                Material: <strong>{modalReceber.modelo}</strong>
              </p>
              <div className="m-form-group">
                <label className="m-form-label">Valor unitário recebido:</label>
                <input
                  type="number"
                  step="0.01"
                  className="m-form-input"
                  value={valorRecebimento}
                  onChange={(e) => setValorRecebimento(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="m-form-actions" style={{ marginTop: 16 }}>
                <button className="m-btn-secondary" onClick={handleFecharReceber} disabled={salvandoRecebimento}>
                  Cancelar
                </button>
                <button className="m-btn-primary" onClick={handleConfirmarRecebimento} disabled={salvandoRecebimento}>
                  {salvandoRecebimento ? "Salvando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Editar - Mobile */}
        {modalEditar && (
          <div className="m-modal-overlay" onClick={handleFecharEditar}>
            <div className="m-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: 16, fontSize: "1.1rem" }}>Editar Pedido</h3>
              <div className="m-form-group">
                <label className="m-form-label">Modelo / Descrição</label>
                <input
                  type="text"
                  className="m-form-input"
                  value={formEditar.modelo}
                  onChange={(e) => setFormEditar((prev) => ({ ...prev, modelo: e.target.value }))}
                />
              </div>
              <div className="m-form-row">
                <div className="m-form-group" style={{ flex: 1 }}>
                  <label className="m-form-label">Quantidade</label>
                  <input
                    type="number"
                    className="m-form-input"
                    value={formEditar.quantidade}
                    onChange={(e) => setFormEditar((prev) => ({ ...prev, quantidade: e.target.value }))}
                  />
                </div>
                <div className="m-form-group" style={{ flex: 1 }}>
                  <label className="m-form-label">Valor Unit.</label>
                  <input
                    type="number"
                    step="0.01"
                    className="m-form-input"
                    value={formEditar.valorUnitario}
                    onChange={(e) => setFormEditar((prev) => ({ ...prev, valorUnitario: e.target.value }))}
                  />
                </div>
              </div>
              <div className="m-form-actions" style={{ marginTop: 16 }}>
                <button className="m-btn-secondary" onClick={handleFecharEditar} disabled={salvandoEdicao}>
                  Cancelar
                </button>
                <button className="m-btn-primary" onClick={handleSalvarEdicao} disabled={salvandoEdicao}>
                  {salvandoEdicao ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo={tituloPagina} subtitulo="">
      {conteudo}
    </LayoutDesktop>
  );
}

export default HistoricoPedidosPage;
